import type { TablePaginationConfig } from 'antd'
import type { PaginationConfig } from 'antd/es/pagination'
import { useTranslation } from 'react-i18next'

import { useQueryUpdate } from './useQueryUpdate'
import {
  DEFAULT_PER_PAGE,
  DEFAULT_PER_PAGE_OPTIONS
} from './useTableQueryState'

export type PaginationTypes<T> = {
  count?: number
  current_page?: number
  total_pages?: number
  per_page?: number
  page_size?: number
  results?: T
}

export const defaultPageSizeOptions = DEFAULT_PER_PAGE_OPTIONS.map(String)

type PageSizeOptions = NonNullable<TablePaginationConfig['pageSizeOptions']>

export type UsePaginationConfigOptions = {
  defaultPageSize?: number
  pageSizeOptions?: PageSizeOptions
}

const getMinPageSizeOption = (pageSizeOptions: PageSizeOptions) => {
  const numericOptions = pageSizeOptions
    .map(Number)
    .filter((pageSizeOption) => Number.isFinite(pageSizeOption))

  return numericOptions.length > 0
    ? Math.min(...numericOptions)
    : Number(defaultPageSizeOptions[0])
}

const resolvePaginationOptions = (
  options?: number | UsePaginationConfigOptions
): Required<Pick<UsePaginationConfigOptions, 'pageSizeOptions'>> &
  Pick<UsePaginationConfigOptions, 'defaultPageSize'> => {
  if (typeof options === 'number') {
    return {
      defaultPageSize: options,
      pageSizeOptions: defaultPageSizeOptions
    }
  }

  return {
    defaultPageSize: options?.defaultPageSize,
    pageSizeOptions: options?.pageSizeOptions ?? defaultPageSizeOptions
  }
}

const singlePageItemRender: TablePaginationConfig['itemRender'] = (
  _page,
  type,
  originalElement
) => {
  if (
    type === 'page' ||
    type === 'prev' ||
    type === 'next' ||
    type === 'jump-prev' ||
    type === 'jump-next'
  ) {
    return null
  }

  return originalElement
}

export const getPaginationVisibilityConfig = (
  total: number | undefined,
  pageSize: number,
  pageSizeOptions: PageSizeOptions = defaultPageSizeOptions
): Pick<
  TablePaginationConfig,
  'hideOnSinglePage' | 'itemRender' | 'showSizeChanger'
> => {
  const isSinglePageWithRows =
    total !== undefined && total > 0 && total <= pageSize
  const showSizeChanger =
    total !== undefined && total > getMinPageSizeOption(pageSizeOptions)

  return {
    hideOnSinglePage: showSizeChanger ? !isSinglePageWithRows : true,
    itemRender:
      showSizeChanger && isSinglePageWithRows
        ? singlePageItemRender
        : undefined,
    showSizeChanger
  }
}

export const usePaginationConfig = <T>(
  paginatedData?: PaginationTypes<T>,
  options?: number | UsePaginationConfigOptions
): TablePaginationConfig => {
  const { t } = useTranslation()
  const { params, updateQuery } = useQueryUpdate()
  const { defaultPageSize, pageSizeOptions } = resolvePaginationOptions(options)
  const pageSizeFromParams = Number(params.page_size ?? params.per_page)

  const pageSize =
    paginatedData?.per_page ??
    paginatedData?.page_size ??
    (pageSizeFromParams || undefined) ??
    defaultPageSize ??
    DEFAULT_PER_PAGE
  const currentPage = paginatedData?.current_page ?? (Number(params.page) || 1)
  const total = paginatedData?.count
  const paginationVisibilityConfig = getPaginationVisibilityConfig(
    total,
    pageSize,
    pageSizeOptions
  )

  const showTotal = (total: number) => {
    const start = (currentPage - 1) * pageSize + 1
    const end = Math.min(currentPage * pageSize, total)

    return t('pagination.total', { from: start, to: end, total })
  }

  const onChange = (page: number, pageSize: number) => {
    updateQuery({
      ...params,
      page,
      page_size: pageSize,
      per_page: pageSize
    })
  }

  return {
    placement: ['bottomEnd'],
    total,
    pageSize,
    current: currentPage,
    ...paginationVisibilityConfig,
    showTotal,
    pageSizeOptions,
    locale: { items_per_page: '' },
    onChange
  }
}

export const usePaginationListConfig = <T>(
  list?: PaginationTypes<T>
): PaginationConfig => {
  return {
    total: list?.count,
    pageSize: list?.per_page,
    current: list?.current_page,
    pageSizeOptions: defaultPageSizeOptions
  }
}
